import { z } from 'zod';
import { defineFeature } from '../core/defineFeature.js';
import { FeatureType } from '../core/types.js';
const DataAgent = { id: 16, name: 'Data Agent' };
export const COMPOUND_QUERY_MODES = ['Disabled', 'Observe', 'Enabled'];
export const dataAgentCompoundMode = defineFeature({
    type: FeatureType.String,
    propName: 'data_agent_compound_mode',
    uiName: 'dataAgentCompoundMode',
    displayName: 'Compound Query Mode',
    description: 'Controls the compound-query pipeline behavior. "Disabled" — no compound-query code runs. "Observe" — classifier and decomposer run for telemetry, but the result is discarded and the single-envelope path is used. "Enabled" — full pipeline, compound results served to users.',
    category: DataAgent,
    areas: ['application', 'brand'],
    codec: 'identity',
    schema: z.string(),
    default: 'Disabled',
    validValues: COMPOUND_QUERY_MODES,
    isSystem: true,
    flatModelSchema: z.enum(COMPOUND_QUERY_MODES).nullable(),
});
export const dataAgentCompoundEligible = defineFeature({
    type: FeatureType.Boolean,
    propName: 'data_agent_compound_eligible',
    uiName: 'dataAgentCompoundEligible',
    displayName: 'Compound Query Eligible',
    description: 'Controls whether compound query decomposition is available. When disabled, questions are always answered using the standard single-query path.',
    category: DataAgent,
    areas: ['data-connection'],
    applicability: {
        providers: ['CUBEJS'],
    },
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
});
export const dataAgentDynamicInsights = defineFeature({
    type: FeatureType.Boolean,
    propName: 'data_agent_dynamic_insights',
    uiName: 'dataAgentDynamicInsights',
    displayName: 'Dynamic Insights',
    description: 'When enabled, the data agent attempts dynamic query generation when no pre-configured query matches. When disabled, only pre-configured template and guided queries are used.',
    category: DataAgent,
    areas: ['agent'],
    codec: 'identity',
    schema: z.boolean(),
    default: true,
    isSystem: true,
    surfacedIn: ['agent'],
});
export const dataAgentAiContextLayer = defineFeature({
    type: FeatureType.Json,
    propName: 'data_agent_ai_context_layer',
    uiName: 'dataAgentAiContextLayer',
    displayName: 'AI Context Layer',
    description: 'Configure the AI context layer. When enabled, an external API endpoint is called to fetch additional context before returning results to the parent agent. Requires a valid API-type data connection and an endpoint path.',
    category: DataAgent,
    areas: ['agent'],
    codec: 'identity',
    schema: z
        .object({
        enabled: z.boolean(),
        connectionIdentifier: z.string().nullable(),
        path: z.string().nullable(),
        type: z.string().nullable().optional(),
    })
        .nullable(),
    default: null,
    isSystem: true,
    surfacedIn: ['agent'],
    editor: 'data-agent-ai-context-layer',
});
export const dataAgentCompoundLlmClassifier = defineFeature({
    type: FeatureType.Boolean,
    propName: 'data_agent_compound_llm_classifier',
    uiName: 'dataAgentCompoundLlmClassifier',
    displayName: 'Compound Query LLM Classifier',
    description: 'Enables LLM-based classification for compound queries. When enabled, ambiguous questions that the regex pre-filter cannot confidently classify are sent to a lightweight LLM call. When disabled, only the regex-based heuristic is used.',
    category: DataAgent,
    areas: ['data-connection'],
    applicability: {
        providers: ['CUBEJS'],
    },
    codec: 'identity',
    schema: z.boolean(),
    default: false,
    isSystem: true,
});
export const dataAgentFeatures = [
    dataAgentCompoundMode,
    dataAgentCompoundEligible,
    dataAgentDynamicInsights,
    dataAgentAiContextLayer,
    dataAgentCompoundLlmClassifier,
];
